/**
 * 日主性格画像引擎 v2
 * 基于五因子动态生成：日干 + 月令 + 身强弱 + 主导十神 + 是否成格
 * 输出四大板块：核心气质 / 行为风格 / 压力下表现 / 关系模式
 */

import {
  type Stem, type Branch, type Element, type FourPillars,
  STEM_ELEMENT,
} from '../../baziCalc';
import {
  getShishen, calcShishenDistribution, type Shishen,
} from './shishenEngine';
import { judgeGeju, type GejuResult } from './gejuEngine';
import { calcDayStrength } from './xiyongEngine';
import { ELEMENT_COLORS } from './shishenEngine';
import {
  WANGXIANG_TABLE,
  STEM_YIN_YANG,
} from '@/utils/calcTools/unifiedRules';

// ── 职业方向推荐（日干 + 月令 + 主导十神 + 身强弱）────────────────
interface CareerRec {
  industries: string[];
  note: string;
}

// 日干基础行业池
const BASE_CAREER: Record<Stem, string[]> = {
  甲: ['企业管理', '创业', '政界公职', '法律咨询', '教育培训', '林业园艺'],
  乙: ['文艺创作', '教育', '外交公关', '人力资源', '媒体传播', '咨询顾问'],
  丙: ['公关营销', '演艺娱乐', '销售', '品牌策划', '教育培训', '公益组织'],
  丁: ['艺术设计', '心理咨询', '写作编辑', '教育培训', '医疗健康', '手工艺品'],
  戊: ['房地产开发', '建筑工程', '仓储物流', '金融管理', '农业', '保险'],
  己: ['行政管理', '医疗护理', '社工服务', '教育培训', '人力资源', '餐饮酒店'],
  庚: ['军警安保', '工程技术', '竞技体育', '法律仲裁', '金属加工', 'IT开发'],
  辛: ['媒体传播', '艺术设计', '珠宝设计', '科研技术', '咨询分析', '金融理财'],
  壬: ['金融投资', '战略咨询', '国际贸易', '科研教育', '外交事务', '航运物流'],
  癸: ['心理咨询', '文学创作', '医疗服务', '教育培训', '文化研究', '艺术疗愈'],
};

// 月令调整行业权重
const MONTH_CAREER: Record<Branch, { boost: string[]; dampen: string[] }> = {
  寅: { boost: ['教育培训', '创业', '林业园艺', '公益组织'], dampen: ['仓储物流', '保险'] },
  卯: { boost: ['文艺创作', '外交公关', '人力资源', '媒体传播'], dampen: ['工程技术', '金属加工'] },
  辰: { boost: ['房地产', '金融管理', '农业', '建筑工程'], dampen: ['心理咨询', '艺术创作'] },
  巳: { boost: ['公关营销', '销售', '品牌策划', '教育培训'], dampen: ['仓储物流', '航运物流'] },
  午: { boost: ['演艺娱乐', '教育培训', '公益组织', '行政管理'], dampen: ['科研技术', '咨询分析'] },
  未: { boost: ['行政', '房地产', '农业', '建筑工程'], dampen: ['金融投资', '国际贸易'] },
  申: { boost: ['金融管理', '珠宝设计', '法律仲裁', 'IT开发'], dampen: ['教育培训', '公益组织'] },
  酉: { boost: ['艺术设计', '媒体传播', '科研技术', '金融理财'], dampen: ['销售', '公关营销'] },
  戌: { boost: ['房地产', '建筑工程', '仓储物流', '农业'], dampen: ['心理咨询', '创作'] },
  亥: { boost: ['战略咨询', '教育培训', '国际贸易', '文化研究'], dampen: ['军警安保', '竞技体育'] },
  子: { boost: ['金融投资', '科研教育', '外交事务', '航运物流'], dampen: ['演艺娱乐', '行政管理'] },
  丑: { boost: ['教育培训', '金融管理', '房地产', '农业'], dampen: ['销售', '公关营销'] },
};

// 主导十神进一步筛选
const SHISHEN_CAREER: Record<Shishen, { boost: string[]; note: string }> = {
  正官: { boost: ['企业管理', '政界公职', '法律咨询', '行政管理', '教育培训'], note: '正官为吉神，主规矩与秩序，适合规章明确、晋升路径清晰的组织环境。官星清纯者仕途顺遂；官杀混杂则需印星化杀或食伤制杀以通关。' },
  七杀: { boost: ['军警安保', '竞技体育', '创业', 'IT开发', '工程技术'], note: '七杀为凶神，主魄力与挑战，适合高压、竞争性强的领域。七杀无制则激进冒进，有制（食伤或印星）则调和为用，威名远播。' },
  正印: { boost: ['教育培训', '科研教育', '医疗护理', '文化研究', '心理咨询'], note: '正印主涵养与学识，适合需要长期积累的知识型行业。印旺则行动偏慢，需配合食伤泄秀以增强产出效率。' },
  偏印: { boost: ['科研技术', '珠宝设计', '艺术创作', '咨询分析', 'IT开发'], note: '偏印主独特思维与钻研精神，适合独立性强、需要差异化视角的领域。常规路径难以发挥其效，非传统路线反而更容易成功。' },
  食神: { boost: ['艺术设计', '餐饮酒店', '教育培训', '写作编辑', '公益组织'], note: '食神主才华流露与巧思，适合需要创意展现与生活品质的领域。食神泄秀为吉，但食伤过重需防言语惹祸，宜以财星转化才华。' },
  伤官: { boost: ['创业', '公关营销', '媒体传播', '演艺娱乐', '品牌策划'], note: '伤官主突破与创新，适合自由度高、鼓励表达的行业。伤官见官之年是非口舌多，印星流年是伤官格的最佳发展窗口。' },
  正财: { boost: ['金融管理', '房地产', '仓储物流', '保险', '农业'], note: '正财主稳健积累，适合稳定经营型行业。身强财旺则富，身弱财旺则累，格局成败看身财是否平衡。' },
  偏财: { boost: ['国际贸易', '销售', '投资顾问', '公关营销', '餐饮酒店'], note: '偏财主流动与机会，适合需要人脉与商业嗅觉的领域。偏财喜食伤生财，食伤旺则财源广进；比劫旺之年需防争财破耗。' },
  比肩: { boost: ['创业', '技术咨询', '自由职业', '竞技体育', 'IT开发'], note: '比肩主独立与自主，适合能独立完成或与人平等协作的岗位。比劫过重则竞争加剧，需官杀约束方能稳定。' },
  劫财: { boost: ['军警安保', '竞技体育', '创业', '销售', '工程技术'], note: '劫财主行动力与冲劲，适合竞争性强的领域。劫财为凶神，无制则易因冲动而损财，宜以官杀制劫、以食伤生财转化劫性。' },
  日主: { boost: [], note: '' },
};

// 身强弱对职业选择的建议
const STRENGTH_CAREER: Record<'strong' | 'weak' | 'neutral', string> = {
  strong: '身强能量充沛，足以任财官、抗官杀，适合承担竞争性与开创性较强的工作。身强喜泄，宜选择食伤生财或官杀制身类岗位，以泄秀为用，避免印比过旺导致刚愎。',
  weak: '身弱任财官之力有限，宜从事技术、服务、辅助型岗位，不宜独担大局。身弱喜扶，宜选择有团队支撑、体制保障的环境，借印星与比劫之力放大个人价值。',
  neutral: '身主中和，五行力量均衡，管理与技术型岗位皆可胜任。中和之格最忌流年打破平衡，职业选择宜保持弹性，旺年可进取、衰年宜守成。',
};

function calcCareerSuggestions(
  stem: Stem, monthBranch: Branch, dominantShishen: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral',
): CareerRec {
  const base = new Set(BASE_CAREER[stem]);
  const month = MONTH_CAREER[monthBranch];
  const shishen = SHISHEN_CAREER[dominantShishen];

  // 计算得分
  const scores: Record<string, number> = {};
  for (const ind of base) scores[ind] = (scores[ind] ?? 0) + 1;
  for (const ind of month.boost) scores[ind] = (scores[ind] ?? 0) + 1;
  for (const ind of shishen.boost) scores[ind] = (scores[ind] ?? 0) + 2;
  for (const ind of month.dampen) scores[ind] = (scores[ind] ?? 0) - 1;

  // 按得分排序取前5
  const sorted = Object.entries(scores)
    .filter(([_, s]) => s > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name)
    .slice(0, 5);

  // 兜底保证至少3个
  const industries = sorted.length >= 3 ? sorted : [...sorted, ...BASE_CAREER[stem].slice(0, 5 - sorted.length)];

  const note = `${shishen.note} ${STRENGTH_CAREER[bodyStrength]}`;
  return { industries, note };
}

// ── 调和与改善建议（格局 + 身强弱）────────────────────────────────
interface PressureRemedy {
  methods: string[];
  dailyTip: string;
}

const GEJU_REMEDY: Record<string, { methods: string[]; color: string }> = {
  正官格: {
    methods: [
      '宜以制度与流程化解变数，凡事立规在先，条理化是正官格最有效的安定手段。',
      '遇到冲击时，先整理优先级，按步推进，不宜同时推进多条战线。',
      '官杀混杂之年月，宜减少主动挑战上级或体制的动作，以守为主。',
    ],
    color: '#7BC67E',
  },
  七杀格: {
    methods: [
      '七杀为凶神，宜以食伤制杀或印星化杀为解。压力当前，优先制化而非硬抗。',
      '命局若无食伤印星，则需借大运流年之力，在食伤、印星当令时主动出击。',
      '身弱遇杀，最忌以财滋杀，此时宜断舍离财缘，集中力量于化杀之事。',
      '七杀格最宜以目标拆解压力——将大敌拆为小目标，逐个击破即是制杀。',
    ],
    color: '#D14B45',
  },
  正印格: {
    methods: [
      '印星主涵养与学识，遇困时回归阅读、进修、拜师，以印调和最为稳妥。',
      '忌在印星当令时过度依赖经验——印旺则懒，需主动设定 deadlines 逼自己出成果。',
      '母星过旺之年，易因思虑过多而停滞，此时宜以比劫泄印，找同辈协作推进。',
    ],
    color: '#4A90D9',
  },
  偏印格: {
    methods: [
      '偏印主独立思辨，遇困时独处分析优于群体讨论，宜给自己划定思考空间。',
      '偏印格易陷思维死循环，调和之法在"写出来"——将混沌思绪具象为文字或图表。',
      '偏印遇杀，宜以偏门技艺或独特视角化解，常规路径反而越陷越深。',
    ],
    color: '#8A7B91',
  },
  食神格: {
    methods: [
      '食神主才华流露，遇困时宜以创作、策划、技艺展示等方式化解僵局。',
      '食神制杀是最有效的解困格局，发挥才华与创意即可将压力转化为成果。',
      '忌在食神当令之年沉溺享乐，食伤生财方为正道，空耗才华则运势下沉。',
    ],
    color: '#E8A020',
  },
  伤官格: {
    methods: [
      '伤官主突破与创新，遇困时最忌按规矩办事，需打破常规才能找到出路。',
      '伤官见官之年月，是非口舌多，宜少说多做、避锋藏拙，待印星流年再图进取。',
      '伤官生财是最佳化解路径——将突破力导向具体的商业或项目产出。',
    ],
    color: '#E8A020',
  },
  正财格: {
    methods: [
      '正财主稳健积累，遇困时盘点资源、收缩战线、聚焦核心利益为上策。',
      '财格之人忌贪多求全，调和之法在"舍"——减少无效投入，集中优势兵力。',
      '财多身弱之年，宜借助比劫之力，寻找合作伙伴共同承担，不可独撑大局。',
    ],
    color: '#7BC67E',
  },
  偏财格: {
    methods: [
      '偏财主流动与机会，遇困时扩大信息渠道、增加人脉接触面，机会在流动中产生。',
      '偏财格调和之忌在"守"——固步自封是偏财格最大的运势杀手，宜动不宜静。',
      '偏财遇劫，宜以食伤生财化解，发挥才华创造新收入来源。',
    ],
    color: '#A9744F',
  },
  比劫格: {
    methods: [
      '比劫主独立与竞争，遇困时可寻志同道合者分担压力，但忌与同类正面冲突。',
      '比劫过重之年，宜以官杀约束自我——设定明确纪律与期限，以规矩制比劫之散漫。',
      '比劫格化解的核心在"借力"，但借力的前提是选对合作者，比劫太杂反成拖累。',
    ],
    color: '#D4AF37',
  },
  从强格: {
    methods: [
      '从强格以旺为用，遇困时切忌逆势而为——强化自身优势领域，回避与旺神对抗的方向。',
      '从强格最忌见财官破格，调和之法在识别破格流年，该年宜收缩防守、不妄动。',
      '从强格的成功路径是"深"而非"广"，集中力量在优势领域做到极致。',
    ],
    color: '#D14B45',
  },
  从弱格: {
    methods: [
      '从弱格以弱为用，遇困时主动借力是智慧——寻找官杀印星旺的流年贵人协助。',
      '从弱格最忌见比劫帮身破格，调和之法在减少独立承担，善用体制与团队资源。',
      '从弱格的成功路径在"合"——融入大势、顺应潮流，以柔化刚。',
    ],
    color: '#4A90D9',
  },
  从财格: {
    methods: [
      '从财格以财为用，遇困时将注意力转向资源整合与变现路径，务实行动优于空想。',
      '从财格最忌见比劫争财，调和之法在分利——通过合作让渡部分利益以保全大局。',
      '财旺之流年是从财格的进攻窗口，宜在该窗口期内集中推进商业目标。',
    ],
    color: '#E8A020',
  },
  从杀格: {
    methods: [
      '从杀格以杀为用，遇困时压力本身即是机遇——设定明确目标与期限，以纪律对抗混乱。',
      '从杀格最忌见食伤制杀破格，调和之法在收敛锋芒、强化服从与执行力。',
      '从杀格的抗压天赋远超常人，极限环境中反而能激发最大潜能，宜主动选择高压赛道。',
    ],
    color: '#D14B45',
  },
  从儿格: {
    methods: [
      '从儿格以食伤为用，遇困时以创意与才华突围——将困局转化为创作素材或项目灵感。',
      '从儿格最忌见印星克制食伤，调和之法在减少理论学习、增加实践产出。',
      '食伤生财是从儿格的通关路径，才华变现是最有效的解困手段。',
    ],
    color: '#E8A020',
  },
  化气格: {
    methods: [
      '化气格主灵活变通，遇困时不宜固守单一方案，应准备多套预案并行试探。',
      '化气格调和之忌在"执念"——接受变化本身就是此格的最大优势，变则通。',
      '化气格宜多方交流，融合不同领域视角，跨界思维是最有效的破局手段。',
    ],
    color: '#7BC67E',
  },
};

const STRENGTH_REMEDY: Record<'strong' | 'weak' | 'neutral', { methods: string[]; dailyTip: string }> = {
  strong: {
    methods: [
      '身强宜泄不宜补，调和之法在"泄"——以食伤生财、以官杀制身，将过剩能量导向产出。',
      '身强忌再逢印比流年，该年易因固执或冒进而受挫，宜以守为主、减少扩张。',
      '身强之人化解压力的最佳方式是"做事"——通过高强度输出消耗能量，运动、创作、项目推进皆可。',
    ],
    dailyTip: '身强之人每日宜保持输出节奏，静坐冥想非最佳选择，以行动泄力方能平衡。',
  },
  weak: {
    methods: [
      '身弱宜补不宜泄，调和之法在"扶"——以印星生身、以比劫帮身，借力使力。',
      '身弱最忌财星过旺之年，财多身弱反成负担，该年宜收缩财务目标、减少支出。',
      '身弱之人化解压力需"养"——充足睡眠、减少无效消耗、寻找能量互补的合作伙伴。',
    ],
    dailyTip: '身弱之人每日宜留出恢复时段，避免连续高强度输出，间歇性休整方能持久。',
  },
  neutral: {
    methods: [
      '身主中和，调和之法在"调"——根据流年旺衰灵活调整策略，旺年进取、衰年守成。',
      '中和格最忌打乱平衡，遇困时需同时兼顾补与泄，不宜单一路径。',
      '中和之人宜培养一到两项可持续的能量调节手段，如规律运动或固定作息。',
    ],
    dailyTip: '中和之人每日宜做一次状态评估，根据当日能量高低调整任务强度，保持动态平衡。',
  },
};

function calcPressureRemedy(
  geju: GejuResult, bodyStrength: 'strong' | 'weak' | 'neutral',
): PressureRemedy {
  const gejuRemedy = GEJU_REMEDY[geju.name] ?? GEJU_REMEDY['比劫格'];
  const strengthRemedy = STRENGTH_REMEDY[bodyStrength];

  // 合并方法：格局方法 + 身强弱方法，去重后取最多5条
  const allMethods = [...gejuRemedy.methods, ...strengthRemedy.methods];
  const uniqueMethods: string[] = [];
  for (const m of allMethods) {
    if (!uniqueMethods.includes(m)) uniqueMethods.push(m);
  }

  return {
    methods: uniqueMethods.slice(0, 5),
    dailyTip: strengthRemedy.dailyTip,
  };
}

// ── 导出的 PersonalityProfile 接口更新 ───────────────────────
export interface PersonalityProfile {
  stem: Stem;
  element: Element;
  elementColor: string;
  monthBranch: Branch;
  bodyStrength: 'strong' | 'weak' | 'neutral';
  bodyStrengthLabel: string;
  dominantShishen: Shishen;
  geju: GejuResult;
  coreTemperament: string;
  behaviorStyle: string;
  underPressure: string;
  relationshipMode: string;
  careerSuggestions: CareerRec;
  pressureRemedy: PressureRemedy;
  tags: Array<{ label: string; color: string }>;
}

// ── 天干基础人格底色 ───────────────────────────────────────
const BASE_CORE: Record<Stem, string> = {
  甲: '甲木参天，刚直向上，禀受东方生气。甲木之人日主刚强，具领袖气象，目标明确后不易动摇。行事磊落，重原则轻权变，在格局清正时为栋梁之才，在格局混杂时易因固执受挫。',
  乙: '乙木曲直，藤蔓之性，禀受东方柔木之气。乙木之人外柔内韧，善于借势攀援、迂回达成目标。适应力强于甲木，但缺乏甲木的定见，遇强势格局易随风倒，需依托甲木或庚金以立根基。',
  丙: '丙火炎上，太阳之象，禀受南方烈火之气。丙火之人热情外露，感染力极强，所到之处皆能升温。喜生旺之地，忌水旺克身，水重火熄则斗志消沉，需木来通关生火。',
  丁: '丁火柔中，灯烛之光，禀受南方阴火之气。丁火之人细腻敏感，内心世界层次丰富，对品质与细节有天然要求。丁火需甲木为源方能持久发光，无木则火微易灭，精神易陷入低迷。',
  戊: '戊土厚重，堤岸之土，禀受中央阳土之气。戊土之人沉稳持重，守信不欺，是最能承担长期责任的日主类型。戊土喜木疏土、水滋润，纯土无制则板结不通，事业发展缓慢。',
  己: '己土卑湿，田园之土，禀受中央阴土之气。己土之人温和包容，善于在人际中调和矛盾，有天然的协调天赋。己土最怕水泛土流，水多则需火来生土以固根基。',
  庚: '庚金刚健，剑戟之金，禀受西方阳金之气。庚金之人果决直接，行动力极强，攻坚克难是其本能。庚金喜火炼方成大器，无火则刚而无用，性格易流于粗暴。',
  辛: '辛金温润，珠玉之金，禀受西方阴金之气。辛金之人内秀灵敏，审美与判断力兼具，追求品质与精确。辛金忌土重埋金，土多则才华被掩，需水来淘洗方能发光。',
  壬: '壬水汪洋，江河奔流，禀受北方阳水之气。壬水之人视野宏阔，善于从全局布局，思维灵活不拘一格。壬水喜戊土为堤以成其用，无制则泛滥无归，易流于空谈。',
  癸: '癸水至弱，雨露之精，禀受北方阴水之气。癸水之人直觉敏锐，情感深沉，善于在潜移默化中影响周遭。癸水最需辛金发源、甲木疏通，无源则干涸，无泄则滞积成患。',
};

const BASE_BEHAVIOR: Record<Stem, string> = {
  甲: '甲木行事目标导向，习惯站在前方引领，亲力亲为不喜绕弯。对效率与结果要求高，决策后执行力强，但格局中若七杀无制，则易急躁冒进、树敌过多。',
  乙: '乙木行事迂回灵活，善于借力使力，不习惯正面硬碰。习惯先观察环境再寻找最佳切入点，但若命局中比劫过重，则易优柔寡断、错失时机。',
  丙: '丙火行事积极主动，带头冲锋，善于点燃团队氛围。对新事物好奇但持久性需看格局配合——印旺则持久，劫旺则三分钟热度。',
  丁: '丁火行事深思熟虑，注重细节与品质，擅长精细化工作。决策前反复斟酌，但一旦决定则十分坚定。若命局食伤受制，则易因过度谨慎而拖延。',
  戊: '戊土行事脚踏实地，按部就班，重视承诺兑现。不喜变化过快的环境，习惯在稳定中积累。若命局中木旺疏土有力，则能在稳健中保持进取。',
  己: '己土行事协调斡旋，照顾大局，善于平衡各方利益。对人际氛围敏感，但若命局中官杀混杂，则易因顾虑太多而进退两难。',
  庚: '庚金行事直来直去，速战速决，执行力极强。在需要魄力的任务中表现出色，但若命局中火弱金寒，则刚而无谋，易因冲动而受挫。',
  辛: '辛金行事精于谋划，注重品质与美感，不喜粗放。在需要精准度的工作中表现出色，但若命局中土重埋金，则才华难以施展、陷入内耗。',
  壬: '壬水行事宏观布局，灵活应变，善于在复杂局势中找到最优路径。但若命局中无戊土制水，则易流于空谈、多谋少决。',
  癸: '癸水行事以柔化之，潜移默化，善于用细腻方式达成目标。但若命局中无木疏通，则癸水滞积，行动力偏弱，计划多于执行。',
};

const BASE_PRESSURE: Record<Stem, string> = {
  甲: '甲木遇压力时本能正面迎战，遇强则强。抗压能力来自其刚直本性，但需防格局中七杀无制——此时压力会转化为冒进，宜以印星化杀、以食伤制杀为解。',
  乙: '乙木遇压力时善于迂回调整，寻找缝隙前进。不会硬碰硬，但若命局中官杀过重而身弱，则易过度回避、委屈求全，此时宜借比劫之力增强底气。',
  丙: '丙火遇压力时越战越勇，热情是最强武器。但需防水旺克火之年——此时能量被压制，热情退去后突然疲惫，宜以木通关生火以续战力。',
  丁: '丁火遇压力时倾向于向内收缩，独自消化情绪。敏感心灵恢复较慢，若命局中印星有力，则能以学识涵养对抗焦虑；无印则易陷入情绪内耗。',
  戊: '戊土遇压力时稳如泰山，耐力是其最大优势。但需防木旺之年疏土太过——此时旧方法失效，需勇于尝试新路径，不可一味死守。',
  己: '己土遇压力时选择隐忍负重，默默承担。善于照顾他人而忽略自己，若命局中比劫有力，则需学会分责；无比劫则身心俱疲。',
  庚: '庚金遇压力时正面硬刚，爆发力惊人。但若命局中火弱，则刚而无制，高压下易伤及人际关系，宜以官星约束、以印星涵养 temper 锋芒。',
  辛: '辛金遇压力时易过度思虑，陷入自我怀疑。追求完美反而成负担，若命局中有水淘洗，则能恢复清明；无水则才华被困、焦虑加深。',
  壬: '壬水遇压力时以智取胜，寻找替代路径。但若命局中无官杀制水，则易选择逃避，此时宜以戊土约束、设定明确边界，直面问题优于绕路。',
  癸: '癸水遇压力时情绪起伏较大，需要倾诉与情感出口。若命局中金水相生太过，则易吸收周围负能量，宜以木泄水、以火调候，建立情绪净化机制。',
};

const BASE_RELATION: Record<Stem, string> = {
  甲: '甲木在关系中主动承担责任，有强烈的保护欲与主导意识。对朋友讲义气，对伴侣忠诚。但若命局中比劫过重，则易强势压人；宜以官杀约束、以食伤泄秀，学会在关系中留有余地。',
  乙: '乙木在关系中善于协调照顾，天然的润滑剂角色。但若命局中身弱官杀重，则易过度迁就、失去自我边界，宜以印星生身增强底气，敢于在关系中表达真实需求。',
  丙: '丙火在关系中慷慨大方，温暖所有人，社交圈广泛。但若命局中比劫争辉，则易让亲密关系缺乏深度，宜以官星收敛、以食伤深化，将最集中的能量留给最重要的人。',
  丁: '丁火在关系中深情专一，追求心灵契合。但若命局中印星过旺，则内敛过深、让伴侣难以接近，宜以食伤疏通、主动分享感受，关系方能真正深入。',
  戊: '戊土在关系中忠诚可靠，用行动表达立场，婚姻稳固性高。但若命局中木弱土板结，则不善表达情感、让伴侣感到疏离，宜以食伤生财、增加情感流动性。',
  己: '己土在关系中体贴入微，善解人意，是理想的调和型伴侣。但若命局中财官混杂，则过于迁就可能在感情中失去自我，宜以比劫帮身、在爱中保持独立边界。',
  庚: '庚金在关系中重义气轻利益，对伴侣直爽干脆。但若命局中火弱金寒，则表达方式过于生硬，宜以官星调候、学会从细节中展现温度，刚柔并济关系更稳。',
  辛: '辛金在关系中重质不重量，对伴侣有较高期望与审美要求。但若命局中土重埋金，则完美主义成为负担，宜以水淘洗、放下"完美滤镜"，接纳关系中的不完美。',
  壬: '壬水在关系中深沉包容，给伴侣充足空间，不易控制对方。但若命局中无木疏通，则不易敞开心扉、让亲近的人感到距离感，宜主动增加情感表达的频率。',
  癸: '癸水在关系中深情滋养，全心投入，情感浓度极高。但若命局中金水相生太过，则情绪起伏大、伴侣需有较强包容力，宜以木泄水、建立情感边界，避免能量单向流失。',
};

// ── 月令季节修饰 ─────────────────────────────────────────
const MONTH_SEASON: Record<Branch, { season: string; effect: string; color: string }> = {
  寅: { season: '春', effect: '寅月木气得时，甲木临官、乙木临官，日主得月令之气则根基稳固，行事冲劲十足。若身强则进取心被辅助，宜主动开拓；身弱则得月令之扶，能量有所回升。', color: '#7BC67E' },
  卯: { season: '春', effect: '卯月木气专旺，甲木帝旺、乙木临官，木之柔韧与生长力达到极盛。若日主为木则身过强，宜以金制木、以火泄秀；若日主非木，则月令木旺对其食伤或官杀产生显著影响。', color: '#7BC67E' },
  辰: { season: '春', effect: '辰月土中藏木，木之余气尚在，土气渐旺。辰为水库，若日主为水则得库气之助，思考力增强；若日主为土则得令而固，稳重性进一步提升。', color: '#A9744F' },
  巳: { season: '夏', effect: '巳月火旺当令，丙火临官、丁火帝旺，火势炎炎。若日主为火则身强而炽，需防水克；若日主为水则官杀压力增大，宜以印星化解或寻比劫帮扶。', color: '#D14B45' },
  午: { season: '夏', effect: '午月火炎极盛，丙火帝旺、丁火临官，火气最烈之时。日主逢午月，若火旺则精力过剩需泄；若火为忌神则身心焦躁，宜以水调候、以土泄火。', color: '#D14B45' },
  未: { season: '夏', effect: '未月火土交融，火气渐退而土气当令。未中藏木火土，若日主为土则得月令之扶，稳重有加；若日主为木则财旺身弱，需防财星过重反成负担。', color: '#E8A020' },
  申: { season: '秋', effect: '申月金旺当令，庚金临官、壬水长生，金水之气渐盛。若日主为金则身强而锐，需火炼方成大器；若日主为木则官杀克身，宜以印星化杀或食伤制杀。', color: '#D4AF37' },
  酉: { season: '秋', effect: '酉月金气专旺，辛金临官、庚金帝旺，金之肃杀与精致力达于极盛。若日主为金则身过强，宜以水泄金、以火炼金；若日主为木则七杀当头，压力显著。', color: '#D4AF37' },
  戌: { season: '秋', effect: '戌月土中藏金，金之余气尚存，土气当令而燥。戌为火库，若日主为火则得库根，根基更稳；若日主为水则官杀土重，压力增加，宜以金泄土或木疏土。', color: '#A9744F' },
  亥: { season: '冬', effect: '亥月水旺当令，壬水临官、甲木长生，水木之气渐盛。若日主为水则身强而泛，需土为堤以成其用；若日主为土则财星过重，身弱难任，宜以火生土为救。', color: '#4A90D9' },
  子: { season: '冬', effect: '子月水气专旺，癸水临官、壬水帝旺，水之深沉与智慧达于极盛。若日主为水则身过强，宜以木泄水、以火调候；若日主为火则官杀克身，需木通关生火。', color: '#4A90D9' },
  丑: { season: '冬', effect: '丑月土中藏水，水之余气尚在，土气寒湿当令。丑为金库，若日主为金则得库根，才华有藏；若日主为火则印星有力但寒湿，需木火并用来调候。', color: '#A9744F' },
};

// ── 身强弱修饰 ───────────────────────────────────────────
const STRENGTH_DESC: Record<'strong' | 'weak' | 'neutral', { label: string; prefix: string; color: string }> = {
  strong: {
    label: '身强',
    prefix: '身强则日主根气充沛，足以任财官、抗官杀。行事有主见，不易被外界左右。但身强忌再逢印比流年——过旺则刚愎自用，需以食伤泄秀或官杀约束方能成器。',
    color: '#D14B45',
  },
  weak: {
    label: '身弱',
    prefix: '身弱则日主根气不足，任财官之力有限，善于灵活应变、借力使力。身弱喜印星生扶、比劫帮身，最忌财旺之年——财多身弱反成拖累，宜收缩财务目标。',
    color: '#4A90D9',
  },
  neutral: {
    label: '中和',
    prefix: '身主中和，五行力量相对均衡，不偏不倚。能根据不同情境调整策略，既不过于强势也不过于柔弱。中和之格最忌流年打破平衡，旺年宜进取、衰年宜守成。',
    color: '#7BC67E',
  },
};

// 身强弱 → 行为风格专属修饰
const STRENGTH_BEHAVIOR: Record<'strong' | 'weak' | 'neutral', string> = {
  strong: '身强者日主根气充沛，行事有主见、有底气，习惯亲自推进、不依赖外力。但身强过旺则刚愎，行为上需防独断专行，宜引入食伤泄秀或官杀约束以增加弹性。',
  weak: '身弱者日主根气不足，任财官之力有限，行为上更习惯借力使力、迂回达成。身弱并非缺陷，善于合作与借势反而能补足能量短板，但需防过度依赖而失去自主判断。',
  neutral: '身主中和，五行力量均衡，行为上可刚可柔，能根据情境灵活切换策略。中和之格最忌流年打破平衡，旺年可大胆进取、衰年宜收缩防守。',
};

// 身强弱 → 压力下表现专属修饰
const STRENGTH_PRESSURE: Record<'strong' | 'weak' | 'neutral', string> = {
  strong: '身强者抗压来自自身根气，遇困时本能硬扛，不轻易向外求助。但身强忌印比流年过旺——此时刚愎加剧，压力反而因拒绝借力而放大，宜适时引入食伤泄力。',
  weak: '身弱者抗压能力有限，遇困时第一反应是寻找支援与庇护。身弱喜印星调和、比劫帮身，压力之下贵人运比硬抗更有效。忌财星过旺之年——财多身弱，压力倍增。',
  neutral: '身主中和，抗压策略宜根据流年灵活调整：身旺之年可主动出击、身衰之年宜收缩防守。中和之格忌单一路径，旺衰交替时需准备两套应对方案。',
};

// 月令 → 行为风格专属修饰
const MONTH_BEHAVIOR: Record<Branch, string> = {
  寅: '寅月木气得时，行为冲劲充足，宜主动开拓。若日主为木则身强，行事更果断；非木日主则食伤或官杀被月令辅助，才华或魄力有所提升。',
  卯: '卯月木气专旺，柔韧与生长力达极盛。行为上善于借势与变通，但若日主为木则身过强，需防过于强势而忽视团队协作。',
  辰: '辰月土气渐旺，行为趋于稳重务实。辰为水库，若日主为水则思维力增强，行为更具策略性；若日主为土则根基更固，执行力提升。',
  巳: '巳月火旺，行为积极主动、热情外露。若日主为火则身强炽烈，需防水克；若日主为水则官杀压力增大，行为上宜收敛锋芒、以印星化解。',
  午: '午月火炎极盛，精力过剩需泄，行为上不宜固守静态任务。若火为忌神则身心焦躁，宜以水调候、以土泄火，保持行为节奏的平衡。',
  未: '未月火土交融，行为协调性强，善于在多方利益中寻找平衡点。若日主为土则稳重有加；若日主为木则财旺身弱，行为上宜收缩战线。',
  申: '申月金旺，行为果决直接，执行力强。若日主为金则身强而锐，需火炼方成大器；若日主为木则官杀克身，行为上宜以印星化杀或食伤制杀。',
  酉: '酉月金气专旺，精致与肃杀力达极盛。行为上追求效率与精准，但若日主为金则身过强，需防过于刚硬而伤及人际关系。',
  戌: '戌月土燥金藏，行为趋于保守防御。戌为火库，若日主为火则根基稳固；若日主为水则官杀土重，压力增加，行为宜以金泄土或木疏土。',
  亥: '亥月水旺，行为灵活善变、宏观布局能力强。若日主为水则身强而泛，需土为堤以成其用；若日主为土则财星过重，身弱难任，行为宜借力。',
  子: '子月水气专旺，深沉与智慧达极盛。行为上善于以智取胜，但若日主为水则身过强，需木泄水、火调候；若日主为火则官杀克身，需木通关生火。',
  丑: '丑月土寒水藏，行为趋于内敛深思。丑为金库，若日主为金则才华有藏；若日主为火则印星有力但寒湿，行为需木火并用以调候。',
};

// 月令 → 压力下表现专属修饰
const MONTH_PRESSURE: Record<Branch, string> = {
  寅: '寅月木气得时，压力下本能向前突破，但若身弱者逢寅月则得月令之扶，抗压能力有所提升。寅月木旺克土，若日主为土则压力增大，宜以火生土化解。',
  卯: '卯月木气极盛，压力下善于迂回调整、寻找缝隙。但若日主为木则身过强，压力反而转化为冒进冲动，需以金制木、以火泄秀。',
  辰: '辰月土旺，压力下稳如泰山，耐力是其优势。但若木旺疏土太过，旧有方法失效，需勇于尝试新路径，不可一味死守。',
  巳: '巳月火旺，压力下越战越勇，热情是最强武器。但若水旺克火之年，能量被压制，热情退去后突然疲惫，宜以木通关生火续力。',
  午: '午月火炎极盛，压力下精力过剩需泄，宜以高强度输出消耗能量。但若火为忌神则身心焦躁，宜以水调候、以土泄火。',
  未: '未月火土交融，压力下选择隐忍负重。但未中藏木火土，若日主为木则财旺身弱，压力下宜收缩战线、减少扩张。',
  申: '申月金旺肃杀，压力下正面硬刚、爆发力惊人。但若火弱金寒，高压下易伤及人际关系，宜以官星调候、以印星涵养。',
  酉: '酉月金气专旺，压力下易过度思虑、陷入自我怀疑。若命局中有水淘洗，则能恢复清明；无水则才华被困、焦虑加深。',
  戌: '戌月土燥金藏，压力下稳守防御。但若日主为水则官杀土重，压力增加，宜以金泄土或木疏土化解。',
  亥: '亥月水旺，压力下以智取胜、寻找替代路径。但若无戊土制水，则易选择逃避，宜以土约束、设定明确边界，直面问题优于绕路。',
  子: '子月水气专旺，压力下情绪起伏较大，需倾诉与情感出口。若金水相生太过，则易吸收周围负能量，宜以木泄水、以火调候，建立情绪净化机制。',
  丑: '丑月土寒水藏，压力下倾向于向内收缩、独自消化。丑中藏金，若日主为金则得库根，才华有藏，能以学识化解焦虑。',
};

// 月令 → 关系模式专属修饰
const MONTH_RELATION: Record<Branch, string> = {
  寅: '寅月木气得时，关系中主动承担责任，保护欲与主导意识强。但若比劫过重则易强势压人，宜以官杀约束、以食伤泄秀，在关系中留有余地。',
  卯: '卯月木气专旺，关系中善于协调照顾，是天然的润滑剂。但若身弱官杀重，则易过度迁就、失去自我边界，宜以印星生身增强底气。',
  辰: '辰月土旺，关系中忠诚可靠，用行动表达立场，婚姻稳固性高。但若木弱土板结，则不善表达情感、让伴侣感到疏离，宜以食伤生财增加情感流动性。',
  巳: '巳月火旺，关系中慷慨大方、温暖所有人，社交圈广泛。但若比劫争辉，则亲密关系缺乏深度，宜以官星收敛、以食伤深化，将最集中的能量留给最重要的人。',
  午: '午月火炎极盛，关系中深情专一但情绪波动大。若火旺则热情过剩，伴侣需有较强包容力，宜以水调候、建立情感边界。',
  未: '未月火土交融，关系中体贴入微、善解人意，是理想的调和型伴侣。但若财官混杂，则过于迁就可能在感情中失去自我，宜以比劫帮身、保持独立边界。',
  申: '申月金旺，关系中重义气轻利益，对伴侣直爽干脆。但若火弱金寒，则表达方式过于生硬，宜以官星调候、从细节中展现温度。',
  酉: '酉月金气专旺，关系中重质不重量，对伴侣有较高期望与审美要求。但若土重埋金，则完美主义成为负担，宜以水淘洗、放下完美滤镜。',
  戌: '戌月土燥，关系中稳重但缺乏浪漫表达。戌为火库，若日主为火则根基稳固，关系忠诚度高；若日主为水则沟通障碍增加，宜以金泄土疏通。',
  亥: '亥月水旺，关系中深沉包容，给伴侣充足空间，不易控制对方。但若无木疏通，则不易敞开心扉，宜主动增加情感表达的频率。',
  子: '子月水气专旺，关系中深情滋养，全心投入，情感浓度极高。若金水相生太过，则情绪起伏大，宜以木泄水、建立情感边界，避免能量单向流失。',
  丑: '丑月土寒，关系中内敛深思，感情表达偏慢热。丑为金库，若日主为金则才华有藏，能以细腻关怀维系关系；若日主为火则需木火调候以增加温暖感。',
};

// ── 主导十神修饰（影响行为与关系）────────────────────────
const SHISHEN_BEHAVIOR: Record<Shishen, string> = {
  正官: '正官主导使行为更重规矩与秩序，决策前有章法可循，不喜无计划之变动。正官为吉神，行事有分寸，但官杀混杂时则束手束脚，需印星化杀方能舒展。',
  七杀: '七杀主导使行为更具魄力与冒险精神，敢于挑战高难度任务。七杀为凶神，无制则激进冒进，有制（食伤或印星）则调和为用，威名远播。',
  正印: '正印主导使行为更稳重内敛，决策前充分调研与思考，重视积累与长期主义。印旺则思虑过多、行动偏慢，需食伤泄秀以增行动力。',
  偏印: '偏印主导使行为更具独特思维与钻研精神，不拘一格，偏好独立探索新路径。偏印为偏门之星，常规路径难以发挥其效，宜走差异化路线。',
  食神: '食神主导使行为更重才华流露与巧思，善于以技艺与创意解决问题，不喜蛮力。食神泄秀为吉，但食伤过旺则反克官星，需防言语或创作惹祸。',
  伤官: '伤官主导使行为更具突破与锋芒，突破常规，追求独特表达。伤官见官之年月是非口舌多，宜收敛锋芒，待印星当令再图进取。',
  正财: '正财主导使行为更重务实与稳健积累，理财有道，不喜投机冒险。正财为稳定之财，但若身弱财旺则财反为累，需比劫帮身方能任财。',
  偏财: '偏财主导使行为更善交际与把握机会，人脉广泛，善于在流动中捕捉商机。偏财为流动之财，但比劫过旺之年需防争财破耗。',
  比肩: '比肩主导使行为更重独立与自主，有主见、不服输，习惯依靠自身力量解决问题。比劫过重则竞争加剧、人际摩擦增多，需官杀约束。',
  劫财: '劫财主导使行为更具行动力与冲劲，竞争意识强，善于在博弈中抢占先机。劫财为凶神，无制则易因冲动而损财，宜以官杀制劫。',
  日主: '',
};

const SHISHEN_RELATION: Record<Shishen, string> = {
  正官: '正官影响下的关系模式以责任与秩序为核心，对待感情与友情皆重承诺。官星为夫星，女命正官透干者婚姻多稳定；男命官星则主事业优先，需防因工作忽略亲密关系。',
  七杀: '七杀影响下的关系模式具保护欲与掌控感，对在乎的人有强烈守护意识。七杀无制则关系紧张、冲突频发；有制（食伤或印星）则转化为深沉而有力的情感纽带。',
  正印: '正印影响下的关系模式重精神交流与共同成长，偏好与有学识、有深度的人交往。印星为母星，印旺者易受长辈影响，择偶时长辈意见权重较高。',
  偏印: '偏印影响下的关系模式需独立空间，不喜被过度依赖，重视彼此的精神边界。偏印之人感情表达内敛，伴侣需理解其独处需求，强求亲密反而适得其反。',
  食神: '食神影响下的关系模式以才华与情趣为纽带，善于通过共同的审美与爱好维系关系。食神泄秀为吉，但若食伤过重则言语锋利，易在关系中因"太会说话"而伤人。',
  伤官: '伤官影响下的关系模式重平等与自由，不喜被束缚，重视伴侣间的思想碰撞。伤官见官之年易生口舌，感情中宜减少争执、以和为贵，待印星流年再谈大事。',
  正财: '正财影响下的关系模式务实稳重，重视家庭责任与物质基础，是稳定的长期伴侣类型。正财为妻星，男命正财透干者对婚姻投入度高，但若身弱财旺则易被感情拖累。',
  偏财: '偏财影响下的关系模式多元活跃，善于营造新鲜感与变化。偏财为父星与流动之财，偏财重者感情中需防因社交过广而让伴侣缺乏安全感，宜适度收敛。',
  比肩: '比肩影响下的关系模式重平等与尊重，不喜单方面付出或接受，追求势均力敌的互动。比劫过重之年易有竞争者介入感情，需以官星约束自我、以印星化解争端。',
  劫财: '劫财影响下的关系模式热烈直接，行动力强，善于为朋友与伴侣挺身而出。劫财为争财之星，感情中需防因冲动或经济纠纷而生摩擦，宜以食伤生财转化劫性。',
  日主: '',
};

// ── 格局修饰（影响核心气质与压力）────────────────────────
const GEJU_CORE: Record<string, string> = {
  正官格: '正官格赋予处世底色以「规矩中正、条理自律」，行事有章法、不喜无预期的变动。',
  七杀格: '七杀格赋予处世底色以「魄力果决、危机意识」，遇挑战不退缩，在逆境中反而精力倍增。',
  正印格: '正印格赋予处世底色以「仁厚温润、长期主义」，学识积累是其最稳固的安全感来源。',
  偏印格: '偏印格赋予处世底色以「独特深邃、不拘一格」，非传统视角是其与生俱来的思维习惯。',
  食神格: '食神格赋予处世底色以「从容优雅、才华流露」，善以巧思化解困境，不喜硬碰。',
  伤官格: '伤官格赋予处世底色以「锋芒毕露、突破本能」，循规蹈矩是其最大的内在阻力。',
  正财格: '正财格赋予处世底色以「务实稳健、积累意识」，行事踏实，有明确的现实感。',
  偏财格: '偏财格赋予处世底色以「八面玲珑、机会敏感」，人际流动中捕捉机遇是其本能。',
  比劫格: '比劫格赋予处世底色以「独立自主、竞争意识」，不甘居下，凡事习惯靠自己解决。',
  从强格: '从强格赋予处世底色以「意志坚定、顺势深耕」，旺极则专注，不宜逆势分散。',
  从弱格: '从弱格赋予处世底色以「随和借力、柔中带韧」，融入大势远比逆流而上更有效。',
  从财格: '从财格赋予处世底色以「务实进取、资源整合」，以利益为导向的现实主义是其核心气质。',
  从杀格: '从杀格赋予处世底色以「抗压如铁、逆境爆发」，越是高压环境越能激发其极限状态。',
  从儿格: '从儿格赋予处世底色以「才华驱动、创意至上」，用创造力解决问题是其最自然的方式。',
  化气格: '化气格赋予处世底色以「圆融灵活、善于合化」，在矛盾中寻找共同点是其天然能力。',
};

const GEJU_PRESSURE: Record<string, string> = {
  正官格: '正官格使压力下更自律、更有条理，能将混乱局面梳理出秩序。但官杀混杂之年压力倍增，宜以印星化杀或食伤制杀为解。',
  七杀格: '七杀格使压力成为激发潜能的催化剂，越是在逆境中越能展现爆发力。但七杀无制则压力反噬，需食伤制杀或印星化杀方能转危为安。',
  正印格: '正印格使压力下更依赖学识与经验，通过冷静分析找到出路。但印星过旺则思虑过多、行动停滞，宜以比劫泄印、找同辈协作推进。',
  偏印格: '偏印格使压力下更倾向于独处思考，在安静中寻找突破灵感。但偏印过重易陷思维死循环，宜将混沌思绪具象化为文字或图表以破局。',
  食神格: '食神格使压力下更靠才华与创意化解困境，用巧思而非硬扛来解决问题。食神制杀是最有效的解困配置，发挥才华即可将压力转化为成果。',
  伤官格: '伤官格使压力下更倾向于突破创新，打破常规寻找新的可能。但伤官见官之年是非口舌多，宜少说多做、避锋藏拙，待印星流年再图进取。',
  正财格: '正财格使压力下更靠稳健积累与踏实应对，不冒进、不投机，稳中求胜。但财多身弱之年财星反成负担，宜借助比劫之力共同承担。',
  偏财格: '偏财格使压力下更靠人脉与灵活应变化解，善于调动资源寻找出路。但偏财遇劫之年需防争财破耗，宜以食伤生财转化劫性。',
  比劫格: '比劫格使压力下更靠自己硬扛，独立性强，但也需学会适时借力。比劫过重之年竞争加剧，宜以官杀约束自我、设定明确纪律。',
  从强格: '从强格使压力下顺势而为，强化自身优势以旺抗旺。但破格之年（见财官）运势急转，宜提前识别破格信号、收缩防守。',
  从弱格: '从弱格使压力下善于借力，寻求官杀印星旺的流年贵人协助。破格之年（见比劫）格局不稳，宜减少独立承担、善用体制资源。',
  从财格: '从财格使压力下以务实态度应对，聚焦商业机会与资源整合。但遇比劫争财之年需防破财，宜分利合作以保全大局。',
  从杀格: '从杀格使压力下意志如铁，将逆境转化为成就的动力。但遇食伤制杀之年破格，宜收敛锋芒、强化服从与执行力以稳格局。',
  从儿格: '从儿格使压力下以才华和创造力突围，用创新化解僵局。但遇印星克制食伤之年才华受限，宜减少理论学习、增加实践产出。',
  化气格: '化气格使压力下灵活转换策略，善于融合多方力量化解危机。但逢冲之年化气受损，宜准备多套预案、避免固守单一方案。',
};

// 格局 → 关系模式专属修饰（聚焦日常关系互动行为，格局感情命理见「八字格局」Tab）
const GEJU_RELATION: Record<string, string> = {
  正官格: '正官格在关系中以「承诺与兑现」为爱的语言，更多用实际行动而非言语表达关心。习惯在规则框架内互动，不善释放情绪，需学会在亲密关系中放下责任滤镜，允许自己与伴侣有非正式的相处方式。',
  七杀格: '七杀格在关系中保护欲与占有欲并重，对在乎的人有全力守护的本能冲动。关系中容易引发权力摩擦，需刻意降低掌控频率、给伴侣更多自主空间，以守护代替管控。',
  正印格: '正印格在关系中偏向「给予型」，习惯提供知识、经验和稳定感。与伴侣沟通时倾向讲道理而非倾听情绪，宜主动练习「先回应感受再解决问题」，避免让亲密关系变成讲堂。',
  偏印格: '偏印格在关系中需要大量独立空间，过于频繁的联系会令其感到消耗。情感表达内敛，伴侣需主动询问才能了解其内心，不可把沉默解读为疏离，允许差异存在是维系感情的关键。',
  食神格: '食神格在关系中以品味与才情为纽带，善用共同的生活美学拉近距离。情感表达自然流畅，但食伤过重者言辞犀利，习惯用幽默或直言代替情绪，需防在不经意间「说准了却说伤了」。',
  伤官格: '伤官格在关系中需要思想碰撞，需要能与其平等辩论的伴侣。不喜被束缚，控制型伴侣是其关系的天然破坏者。需学会在争论中主动示弱，给对方「赢」的机会，以保护关系的温度。',
  正财格: '正财格在关系中以稳定贡献为爱的表达，不善言辞浪漫但实际行动可靠。需防陷入「我已经做了很多，为什么你不满足」的情绪定式，多增加非功利性的情感互动与表达。',
  偏财格: '偏财格在关系中社交边界宽松，善于营造轻松愉快的氛围，但亲密感的建立需要时间。需主动设定「专属时间」与「专属深度」，让伴侣感受到自己在众多关系中的优先级。',
  比劫格: '比劫格在关系中平等意识极强，不愿单方面让步，有时会把「坚持原则」带进不必要的日常角力。需学会在伴侣关系里放下竞争心，共同成长远比分出高下更重要。',
  从强格: '从强格在关系中主导意识明显，天然处于「引领」位置。需学会欣赏伴侣视角的差异，而非用自身标准衡量对方。格局稳固之年认真经营感情效果最佳，不宜在关系中频繁试探边界。',
  从弱格: '从弱格在关系中容易产生依赖感，与有担当的伴侣相处如鱼得水。需培养「边界感」——懂得表达需求但不无限索取，是维持关系健康的核心能力。',
  从财格: '从财格在关系中实用主义色彩浓厚，伴侣价值观与生活方向高度一致才有长期可能。需学会把商场竞争模式和家庭模式做切换，避免把伴侣当成资源来评估。',
  从杀格: '从杀格在关系中意志强硬，对伴侣期待值高。压力大时易将情绪带入关系，需刻意建立「离开工作压力的亲密区间」，在与伴侣相处时切换为较低强度的互动模式。',
  从儿格: '从儿格在关系中以才华与创意为核心吸引力，感情氛围轻松浪漫。需警惕「只输出创意热情、忽视日常维护」的模式，稳定的小事积累比偶发的惊喜更能建立长久信任。',
  化气格: '化气格在关系中擅长调和矛盾，能迅速切换到安抚模式。但过强的调和能力有时会压抑真实需求，需学会在关系中也表达「自己的不满意」，而非一味圆融失去真实自我。',
};

/**
 * 获取日主性格画像（五因子综合）
 */
export function getPersonalityProfile(fourPillars: FourPillars): PersonalityProfile {
  const stem = fourPillars.day.stem;
  const element = STEM_ELEMENT[stem];
  const elementColor = ELEMENT_COLORS[element];
  const monthBranch = fourPillars.month.branch;
  const bodyStrength = calcDayStrength(fourPillars);
  const strengthInfo = STRENGTH_DESC[bodyStrength];

  // 主导十神
  const shishenDist = calcShishenDistribution(fourPillars);
  const dominantShishen = (shishenDist[0]?.name ?? '比肩') as Shishen;

  // 格局
  const geju = judgeGeju(fourPillars);

  // 月令修饰
  const monthMod = MONTH_SEASON[monthBranch];

  // 组合四个板块
  const coreTemperament = combineParagraph([
    `您为${stem}日主，生于${monthBranch}月。`,
    BASE_CORE[stem],
    monthMod.effect,
    STRENGTH_DESC[bodyStrength].prefix,
    GEJU_CORE[geju.name] ?? '',
  ]);

  const behaviorStyle = combineParagraph([
    BASE_BEHAVIOR[stem],
    MONTH_BEHAVIOR[monthBranch],
    SHISHEN_BEHAVIOR[dominantShishen] ?? '',
    STRENGTH_BEHAVIOR[bodyStrength],
  ]);

  const underPressure = combineParagraph([
    BASE_PRESSURE[stem],
    GEJU_PRESSURE[geju.name] ?? '',
    MONTH_PRESSURE[monthBranch],
    STRENGTH_PRESSURE[bodyStrength],
  ]);

  const relationshipMode = combineParagraph([
    BASE_RELATION[stem],
    MONTH_RELATION[monthBranch],
    SHISHEN_RELATION[dominantShishen] ?? '',
    GEJU_RELATION[geju.name] ?? '',
  ]);

  // 新增：职业推荐与调和参考
  const careerSuggestions = calcCareerSuggestions(stem, monthBranch, dominantShishen, bodyStrength);
  const pressureRemedy = calcPressureRemedy(geju, bodyStrength);

  // 标签
  const tags = [
    { label: `${stem}日主`, color: elementColor },
    { label: `${monthBranch}月`, color: monthMod.color },
    { label: strengthInfo.label, color: strengthInfo.color },
    { label: `${dominantShishen}主导`, color: '#E8A020' },
    { label: geju.name, color: '#8A7B91' },
  ];

  return {
    stem,
    element,
    elementColor,
    monthBranch,
    bodyStrength,
    bodyStrengthLabel: strengthInfo.label,
    dominantShishen,
    geju,
    coreTemperament,
    behaviorStyle,
    underPressure,
    relationshipMode,
    careerSuggestions,
    pressureRemedy,
    tags,
  };
}

function combineParagraph(parts: string[]): string {
  const cleaned = parts.map(p => p.trim()).filter(p => p.length > 0);
  // 去重：如果有完全相同的句子，只保留一个
  const unique: string[] = [];
  for (const p of cleaned) {
    if (!unique.includes(p)) unique.push(p);
  }
  return unique.join('');
}

// ── 向后兼容：原有 getRizhuInfo 保留 ───────────────────────
// 以下保留原有代码，确保其他引用不受影响

export interface AspectScore {
  label: string;
  icon: string;
  color: string;
  score: number;
  desc: string;
}

export interface RizhuInfo {
  stem: Stem;
  element: Element;
  elementColor: string;
  title: string;
  subtitle: string;
  overview: string;
  aspects: AspectScore[];
  bannerPrompt: string;
  strengths: string[];
  weaknesses: string[];
}

const RIZHU_DATA: Record<Stem, Omit<RizhuInfo, 'stem' | 'element' | 'elementColor'>> = {
  甲: {
    title: '甲木日主',
    subtitle: '栋梁之木 · 刚直进取',
    overview: '甲木如参天大树，刚直向上，充满生命力。甲木之人性格刚毅正直，富有进取心与领导才能，思想独立，不甘居于人下。天生有强烈的目标感，行事果断，是天生的开拓者与领导者。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#7BC67E', score: 85, desc: '领导力强，进取心十足，适合管理、创业、政界，事业运稳中有升。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 70, desc: '感情专一忠诚，但有时过于强势，婚姻中需学习柔和与妥协之道。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#E8A020', score: 75, desc: '财运稳健，通过努力积累，不喜投机，踏实耕耘可获丰收。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 78, desc: '体质强健，肝胆功能旺盛。注意防止情绪过激，保持心情舒畅有助健康。' },
    ],
    bannerPrompt: 'mystical green forest giant tree energy visualization, towering ancient oak with glowing green light streams, sacred nature power, emerald and deep forest colors, mystical atmosphere, centered composition, dark background, cinematic lighting, ultra detailed digital art',
    strengths: ['领导力强', '目标明确', '行事果断', '正直磊落', '进取心足'],
    weaknesses: ['固执己见', '不善妥协', '情绪易激', '过于强势'],
  },
  乙: {
    title: '乙木日主',
    subtitle: '花草之木 · 柔韧灵动',
    overview: '乙木如花草藤蔓，外柔内韧，适应力极强。乙木之人温柔灵巧，善于协调与应变，处事圆润，善解人意。虽表面柔弱，内心却有坚韧的生命力，能在逆境中以柔克刚，找到自己的生存之道。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#7BC67E', score: 78, desc: '协调能力强，适合文艺、教育、外交等需要灵活应变的领域，贵人缘好。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 82, desc: '感情细腻温柔，善于体贴伴侣，感情关系融洽，婚姻幸福指数高。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#E8A020', score: 72, desc: '财运平稳，善于理财，偶有意外惊喜，靠才能与人脉获得财富。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 75, desc: '整体健康，肝胆需适当关注。保持情绪平稳，避免思虑过多是保健关键。' },
    ],
    bannerPrompt: 'mystical green flowering vine energy visualization, delicate emerald vines with golden luminous particles, flexible and resilient plant energy, soft green and gold colors, ethereal glow, dark background, centered composition, cinematic lighting, ultra detailed digital art',
    strengths: ['适应力强', '善于协调', '外柔内韧', '贵人缘好', '善解人意'],
    weaknesses: ['优柔寡断', '缺乏主见', '过度迁就', '易随波逐流'],
  },
  丙: {
    title: '丙火日主',
    subtitle: '太阳之火 · 热情光明',
    overview: '丙火如太阳，光芒万丈，普照万物。丙火之人热情开朗，充满正能量，天生具有感召力，给周围人带来温暖与活力。行事积极主动，心胸宽广，具有天然的领袖气质和感染力，是众人的精神支柱。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#D14B45', score: 87, desc: '感召力强，适合领导、演艺、公关、销售等需要热情展示的领域，人缘极佳。' },
      { label: '感情', icon: 'ri-heart-line', color: '#E8A020', score: 80, desc: '感情热烈奔放，充满激情，关系中带来无限活力与温暖，需注意持久深化。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 78, desc: '财运活跃，多方进财，但需防止过于豪爽导致漏财，积累意识需加强。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 74, desc: '活力充沛，需注意心脑血管健康，避免过度劳累，保持规律作息。' },
    ],
    bannerPrompt: 'mystical blazing sun fire energy visualization, radiant golden solar flares with warm amber light, sacred fire mandala with brilliant light rays, warm red gold orange colors, powerful energy emanating from center, dark background, cinematic dramatic lighting, ultra detailed digital art',
    strengths: ['感召力强', '热情开朗', '心胸宽广', '积极主动', '人缘极佳'],
    weaknesses: ['三分钟热度', '过于豪爽', '缺乏持久', '情绪外露'],
  },
  丁: {
    title: '丁火日主',
    subtitle: '灯烛之火 · 细腻温润',
    overview: '丁火如灯烛，温暖而持久，光照有限但深具温情。丁火之人聪颖细腻，内心丰富，艺术感强，善于在细节中发现美与意义。性格温柔体贴，待人真诚，在亲密关系中散发独特的温润魅力。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#D14B45', score: 78, desc: '适合艺术、文学、心理、教育等需要细腻感知的领域，才华在安静中绽放。' },
      { label: '感情', icon: 'ri-heart-line', color: '#E8A020', score: 84, desc: '感情深厚专一，重视心灵契合，感情中充满温暖与细腻关怀，婚姻幸福。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 70, desc: '财运稳定，靠才能获财，不善投机，踏实积累是最佳路线。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 76, desc: '整体良好，注意心血管保养，保持情绪稳定，减少焦虑是维护健康的关键。' },
    ],
    bannerPrompt: 'mystical candlelight fire energy visualization, warm golden candle flame with delicate light particles, intimate cozy warmth, amber and deep crimson colors, soft glowing light in dark background, ethereal atmosphere, centered composition, ultra detailed digital art',
    strengths: ['细腻温润', '艺术感强', '感情专一', '待人真诚', '品质追求'],
    weaknesses: ['过度谨慎', '情绪内耗', '行动拖延', '思虑过多'],
  },
  戊: {
    title: '戊土日主',
    subtitle: '大地之土 · 稳重厚实',
    overview: '戊土如广袤山岳，稳重厚实，胸怀宽广。戊土之人性格稳健踏实，信用极佳，为人可靠，是众人信赖的依靠。行事稳中求进，不求浮华，以诚信和踏实积累人生的财富与声誉，是天生的支柱型人才。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#A9744F', score: 82, desc: '踏实可靠，适合管理、金融、房地产、建筑等需要稳定性的领域，口碑好。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 76, desc: '感情忠诚踏实，重视责任，婚姻稳固，需学会在关系中表达更多温柔与浪漫。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 80, desc: '财运稳健，通过踏实努力积累财富，不善投机，房产等实物资产收益好。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 78, desc: '体质厚实，脾胃功能良好。注意适当运动，防止因过于稳定而缺乏活力。' },
    ],
    bannerPrompt: 'mystical mountain earth energy visualization, majestic rocky mountains with warm golden brown light streams, sacred earth power, amber and deep brown earthy colors, stable and grounding energy, dark background, centered composition, cinematic lighting, ultra detailed digital art',
    strengths: ['稳重可靠', '信用极佳', '踏实耐劳', '胸怀宽广', '长期主义'],
    weaknesses: ['变通不足', '情感表达少', '行动偏慢', '守旧保守'],
  },
  己: {
    title: '己土日主',
    subtitle: '田园之土 · 温和内敛',
    overview: '己土如肥沃田园，温和内敛，滋养万物。己土之人心思细腻，善于照顾他人，有天然的亲和力。性格温和包容，善于在人与人之间协调斡旋，是团队中的润滑剂，让人如沐春风。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#A9744F', score: 74, desc: '适合行政、医疗、服务、教育等需要亲和力的领域，人际关系是最大资产。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 82, desc: '感情细腻体贴，善解人意，婚姻关系和谐，是温柔体贴的伴侣。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 70, desc: '财运平稳，靠诚信与人缘获财，需注意过于慷慨可能影响个人财富积累。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 75, desc: '脾胃功能良好，需注意防止思虑过多影响消化，保持轻松心态即可。' },
    ],
    bannerPrompt: 'mystical fertile farmland earth energy visualization, warm golden soil with glowing light particles and gentle green sprouts, nurturing earth energy, soft amber and green colors, gentle light in dark background, centered composition, ultra detailed digital art',
    strengths: ['温和包容', '亲和力强', '善于协调', '体贴入微', '善解人意'],
    weaknesses: ['过于迁就', '缺乏主见', '进退两难', '忽视自我'],
  },
  庚: {
    title: '庚金日主',
    subtitle: '刀剑之金 · 刚健果决',
    overview: '庚金如宝剑刀刃，刚健锐利，行事果断。庚金之人性格直爽豪迈，行动力强，处事果决，有军人般的意志与纪律感。面对困难毫不退缩，竞争意识强，具有很强的执行力与突破力，是天生的行动派。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#D4AF37', score: 85, desc: '执行力强，适合军警、管理、工程、竞技等需要魄力的领域，冲劲十足。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 68, desc: '感情直爽，表达方式直接，需学习温柔与体贴，刚柔并济婚姻更融洽。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 78, desc: '财运稳健，通过行动力与竞争力获财，适合实业与竞争性行业。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 76, desc: '体质刚健，需注意呼吸系统，避免过度劳累，保持情绪平和有助健康。' },
    ],
    bannerPrompt: 'mystical silver sword metal energy visualization, gleaming blade with metallic silver and steel light streams, sharp crystalline sparks, cool silver white colors, powerful and decisive energy, dark background, centered composition, cinematic lighting, ultra detailed digital art',
    strengths: ['执行力强', '果决直爽', '意志坚定', '竞争力强', '重义气'],
    weaknesses: ['情绪冲动', '表达生硬', '缺乏温柔', '刚愎自用'],
  },
  辛: {
    title: '辛金日主',
    subtitle: '钗钏之金 · 精致内秀',
    overview: '辛金如珠宝首饰，精致内秀，聪慧灵敏。辛金之人外表优雅，内心细腻，思维敏锐，极具审美品位。天生聪颖，善于观察分析，才华横溢，是天生的智者与艺术鉴赏家。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#D4AF37', score: 82, desc: '才华横溢，适合媒体、艺术、策略咨询、科研等需要聪慧与品位的领域。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 70, desc: '感情细腻，对伴侣有较高期望，需主动经营，减少情绪上的疑虑，信任是关键。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 76, desc: '理财有道，财运稳健，靠才智与品位获财，适合精品、文创类领域。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 74, desc: '整体良好，需注意肺部与皮肤保养，保持情绪平稳，避免思虑过重。' },
    ],
    bannerPrompt: 'mystical silver precious jewelry metal energy visualization, gleaming silver pearl and crystal light streams, intricate metallic patterns with diamond sparkles, cool silver and white colors, refined elegant energy, dark background, centered composition, cinematic lighting, ultra detailed digital art',
    strengths: ['才华横溢', '审美出众', '思维敏锐', '精致内秀', '分析力强'],
    weaknesses: ['完美主义', '自我怀疑', '情绪敏感', '过度思虑'],
  },
  壬: {
    title: '壬水日主',
    subtitle: '江海之水 · 智慧深邃',
    overview: '壬水如浩瀚江海，深邃广博，智慧非凡。壬水之人聪明绝顶，思维深邃，洞察力强，善于宏观规划与战略布局。胸怀宽广，适应力极强，能在任何环境中找到流动前进的道路，是天生的战略家与谋士。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#4A90D9', score: 88, desc: '战略眼光强，适合金融、咨询、科研、外交等需要深度思维与宏观视野的领域。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 72, desc: '感情深沉，不轻易流露，伴侣需给予耐心与理解，一旦付出则真诚深厚。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 82, desc: '智慧型财运，善于把握大局，金融投资类财运佳，长期积累成效显著。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 75, desc: '肾脏泌尿系统需多关注，注意保暖，避免寒湿。保持情绪流动不积压是关键。' },
    ],
    bannerPrompt: 'mystical deep ocean water energy visualization, vast dark blue ocean with luminous water currents, deep sea wisdom power, deep blue and teal colors, flowing water energy patterns, dark background, centered composition, cinematic lighting, ultra detailed digital art',
    strengths: ['战略眼光', '智慧深邃', '适应力强', '宏观布局', '洞察力强'],
    weaknesses: ['多谋少决', '情感内敛', '易逃避问题', '空谈多于行动'],
  },
  癸: {
    title: '癸水日主',
    subtitle: '雨露之水 · 灵秀细腻',
    overview: '癸水如雨露甘霖，滋润万物，柔情似水。癸水之人内心丰富细腻，直觉敏锐，情感深厚，善于感知和理解他人的情绪。思维灵活，记忆力强，善于在潜移默化中影响周围环境，是天生的感知者与情感智者。',
    aspects: [
      { label: '事业', icon: 'ri-briefcase-line', color: '#4A90D9', score: 76, desc: '适合心理咨询、文学、艺术、医疗等需要情感感知与细腻洞察的领域。' },
      { label: '感情', icon: 'ri-heart-line', color: '#D14B45', score: 83, desc: '感情丰富细腻，体贴入微，深情专注，给伴侣带来独特的情感滋养。' },
      { label: '财运', icon: 'ri-money-cny-circle-line', color: '#7BC67E', score: 70, desc: '财运靠才华与人缘，适合文化、服务等领域，需培养理财意识。' },
      { label: '健康', icon: 'ri-heart-pulse-line', color: '#4A90D9', score: 74, desc: '肾脏与情绪健康需同步关注，保持心情愉悦、作息规律是最好的养生方式。' },
    ],
    bannerPrompt: 'mystical rain dew water energy visualization, delicate crystal raindrops with luminous blue light particles, gentle flowing water streams with sparkling droplets, soft blue and silver colors, ethereal moisture energy, dark background, centered composition, ultra detailed digital art',
    strengths: ['直觉敏锐', '情感丰富', '细腻体贴', '记忆力强', '以柔克刚'],
    weaknesses: ['情绪起伏', '行动力弱', '易吸收负能量', '计划多于执行'],
  },
};

export function getRizhuInfo(stem: Stem): RizhuInfo {
  const data = RIZHU_DATA[stem];
  return {
    stem,
    element: STEM_ELEMENT[stem],
    elementColor: ELEMENT_COLORS[STEM_ELEMENT[stem]],
    ...data,
  };
}

export function getDayStemOverview(stem: Stem): string {
  return RIZHU_DATA[stem]?.overview ?? '';
}

export { ELEMENT_COLORS };

// ── 术数化性格标签 ──────────────────────────────────────────
export interface TraitTag {
  label: string;
  category: 'type' | 'tenGod' | 'pattern' | 'season' | 'balance' | 'risk';
  color: string;
}

export interface TraitSummary {
  typeDesc: string;
  yinYang: string;
  wangShuai: string;
  shishenEffect: string;
  risk: string;
}

// 日主阴阳属性映射
const STEM_YY_SHORT: Record<Stem, string> = {
  甲: '阳木参天', 乙: '阴木藤蔓',
  丙: '阳火炎上', 丁: '阴火柔明',
  戊: '阳土厚重', 己: '阴土卑湿',
  庚: '阳金刚健', 辛: '阴金温润',
  壬: '阳水奔流', 癸: '阴水至弱',
};

// 各日主基础术数化标签
const BASE_TYPE_TAGS: Record<Stem, string[]> = {
  甲: ['刚直不折', '向上生发', '栋梁之材', '独立性强'],
  乙: ['柔韧应变', '借势攀援', '曲中取胜', '善于协调'],
  丙: ['炎上昭明', '普照万物', '热情外显', '引领之光'],
  丁: ['柔明持久', '烛照幽微', '细腻温润', '重质不重量'],
  戊: ['厚重承载', '堤岸之固', '守信不欺', '稳重如山'],
  己: ['卑湿滋养', '田园之沃', '温和包容', '善于调和'],
  庚: ['刚健锐利', '肃杀果断', '攻坚克难', '行动至上'],
  辛: ['温润精致', '珠玉之光', '内秀外雅', '品位至上'],
  壬: ['汪洋奔流', '智慧渊深', '宏观布局', '善变多谋'],
  癸: ['至弱渗透', '雨露滋润', '直觉敏锐', '以柔克刚'],
};

// 十神对性格的术数化标签影响
const SHISHEN_TRAIT_TAGS: Record<Shishen, string[]> = {
  正官: ['循规守矩', '秩序感强', '自律稳重'],
  七杀: ['魄力过人', '危机意识', '攻坚本能'],
  正印: ['学识涵养', '仁厚温润', '长期主义'],
  偏印: ['独辟蹊径', '思维深邃', '不喜随俗'],
  食神: ['才华流露', '优雅从容', '生活品质'],
  伤官: ['锋芒毕露', '突破常规', '表达力强'],
  正财: ['务实稳健', '积累意识', '理财有道'],
  偏财: ['机会敏感', '八面玲珑', '人脉广泛'],
  比肩: ['独立自主', '竞争意识', '不服输'],
  劫财: ['行动力暴', '争强好胜', '资源争夺'],
  日主: [],
};

// 十神对性格的术数化风险标签
const SHISHEN_RISK_TAGS: Record<Shishen, string[]> = {
  正官: ['束手束脚', '过度守规'],
  七杀: ['激进冒进', '树敌过多'],
  正印: ['思虑过多', '行动偏慢'],
  偏印: ['思维偏执', '社交疏离'],
  食神: ['安逸享乐', '言语惹祸'],
  伤官: ['口舌是非', '挑战权威'],
  正财: ['过于保守', '守财失机'],
  偏财: ['投机倾向', '财来财去'],
  比肩: ['刚愎自用', '竞争内耗'],
  劫财: ['冲动破财', '人际摩擦'],
  日主: [],
};

// 格局术数化标签
const GEJU_TRAIT_TAGS: Record<string, string[]> = {
  正官格: ['规矩中正', '仕途气象'],
  七杀格: ['杀伐果决', '逆境爆发'],
  正印格: ['学养深厚', '仁德宽宏'],
  偏印格: ['独学深思', '偏门奇才'],
  食神格: ['才华优雅', '从容不迫'],
  伤官格: ['锋芒创新', '突破桎梏'],
  正财格: ['务实守成', '稳步积累'],
  偏财格: ['机会主义', '资源博弈'],
  比劫格: ['独立自主', '竞争开拓'],
  从强格: ['旺势深耕', '专注极致'],
  从弱格: ['借力使力', '顺势而为'],
  从财格: ['商业敏感', '资源整合'],
  从杀格: ['高压耐受', '极限突破'],
  从儿格: ['才华变现', '创意驱动'],
  化气格: ['圆融变通', '跨界融合'],
};

// 格局术数化风险标签
const GEJU_RISK_TAGS: Record<string, string[]> = {
  正官格: ['官杀混杂', '进退两难'],
  七杀格: ['杀无制化', '压力反噬'],
  正印格: ['印旺则懒', '行动停滞'],
  偏印格: ['思维死锁', '孤独偏执'],
  食神格: ['食伤过重', '言语招祸'],
  伤官格: ['伤官见官', '是非口舌'],
  正财格: ['身弱财旺', '为财所累'],
  偏财格: ['比劫争财', '破耗多端'],
  比劫格: ['比劫夺财', '竞争加剧'],
  从强格: ['破格见财官', '急转直下'],
  从弱格: ['破格见比劫', '格局坍塌'],
  从财格: ['破格见比劫', '财来财去'],
  从杀格: ['破格见食伤', '煞反为祸'],
  从儿格: ['破格见印星', '才华受限'],
  化气格: ['逢冲破格', '化气受损'],
};

// 身强弱术数化标签
const STRENGTH_TRAIT_TAGS: Record<'strong' | 'weak' | 'neutral', string[]> = {
  strong: ['任财官', '抗官杀', '有主见'],
  weak: ['借力使力', '善合作', '灵活变通'],
  neutral: ['可刚可柔', '动态平衡', '旺衰皆宜'],
};

// 月令术数化标签
const MONTH_TRAIT_TAGS: Record<Branch, string[]> = {
  寅: ['春生之气', '生发开拓'],
  卯: ['专旺之月', '极盛反收'],
  辰: ['土旺之季', '稳重务实'],
  巳: ['火炎之气', '热情进取'],
  午: ['阳极之月', '精力过剩'],
  未: ['燥土之月', '协调包容'],
  申: ['金肃之秋', '果决精准'],
  酉: ['金极之月', '精致锐利'],
  戌: ['火库之月', '保守防御'],
  亥: ['水旺之冬', '深沉善谋'],
  子: ['水极之月', '智慧渊深'],
  丑: ['寒土之月', '内敛深思'],
};

// 日主 + 喜忌组合的风险标签
function getXiJiRiskTags(dayStem: Stem, xiElements: Element[], jiElements: Element[]): string[] {
  const tags: string[] = [];
  const dayStemEl = STEM_ELEMENT[dayStem];
  const yy = STEM_YIN_YANG[dayStem];

  // 喜用神弱化的风险
  if (xiElements.length === 0) {
    tags.push('喜用不明');
  }

  // 忌神是否透干（简化判断：看忌神五行是否在天干出现）
  // 这里用元素层面判断，不精确到单个天干
  if (jiElements.includes(dayStemEl)) {
    tags.push('比劫为忌');
  }

  // 日主阴阳过偏
  if (yy === 'yang') {
    if (dayStemEl === '木' || dayStemEl === '火') {
      tags.push('阳刚过盛');
    }
  } else {
    if (dayStemEl === '水' || dayStemEl === '金') {
      tags.push('阴柔过甚');
    }
  }

  return tags;
}

/**
 * 获取术数化性格标签
 */
export function getTraitTags(
  fourPillars: FourPillars,
  xiElements?: Element[],
  jiElements?: Element[],
): TraitTag[] {
  const stem = fourPillars.day.stem;
  const bodyStrength = calcDayStrength(fourPillars);
  const shishenDist = calcShishenDistribution(fourPillars);
  const dominantShishen = (shishenDist[0]?.name ?? '比肩') as Shishen;
  const geju = judgeGeju(fourPillars);
  const monthBranch = fourPillars.month.branch;
  const element = STEM_ELEMENT[stem];

  const tags: TraitTag[] = [];
  const usedLabels = new Set<string>();

  function add(label: string, category: TraitTag['category'], color: string) {
    if (!usedLabels.has(label)) {
      usedLabels.add(label);
      tags.push({ label, category, color });
    }
  }

  // 1. 日主类型标签（深色）
  const baseTags = BASE_TYPE_TAGS[stem];
  for (const t of baseTags) add(t, 'type', ELEMENT_COLORS[element]);

  // 2. 阴阳属性
  add(STEM_YY_SHORT[stem], 'type', '#8A7B91');

  // 3. 身强弱标签
  const sLabel = bodyStrength === 'strong' ? '身强旺相' : bodyStrength === 'weak' ? '身弱待扶' : '身主中和';
  add(sLabel, 'balance', bodyStrength === 'strong' ? '#D14B45' : bodyStrength === 'weak' ? '#4A90D9' : '#7BC67E');
  for (const t of STRENGTH_TRAIT_TAGS[bodyStrength]) add(t, 'balance', '#7BC67E');

  // 4. 月令标签
  for (const t of MONTH_TRAIT_TAGS[monthBranch]) add(t, 'season', '#E8A020');

  // 5. 十神标签
  for (const t of SHISHEN_TRAIT_TAGS[dominantShishen]) add(t, 'tenGod', '#E8A020');
  for (const t of SHISHEN_RISK_TAGS[dominantShishen]) add(t, 'risk', '#D14B45');

  // 6. 格局标签
  const gejuTags = GEJU_TRAIT_TAGS[geju.name] ?? [];
  for (const t of gejuTags) add(t, 'pattern', '#8A7B91');
  const gejuRisk = GEJU_RISK_TAGS[geju.name] ?? [];
  for (const t of gejuRisk) add(t, 'risk', '#D46B6B');

  // 7. 喜忌风险标签
  if (xiElements && jiElements) {
    const xjTags = getXiJiRiskTags(stem, xiElements, jiElements);
    for (const t of xjTags) add(t, 'risk', '#D46B6B');
  }

  return tags;
}

// 日主 + 身强弱 + 月令的旺衰判断
function calcWangShuai(stem: Stem, monthBranch: Branch, bodyStrength: 'strong' | 'weak' | 'neutral'): string {
  const el = STEM_ELEMENT[stem];
  const wangStatus = WANGXIANG_TABLE[el]?.[monthBranch] ?? 2;
  const qiLabels = ['死', '囚', '休', '相', '旺'];
  const qi = qiLabels[wangStatus];

  if (bodyStrength === 'strong') {
    return `${el}在${monthBranch}月得${qi}气，身强根气充沛，任财官抗官杀皆有底气`;
  } else if (bodyStrength === 'weak') {
    return `${el}在${monthBranch}月${qi}气，身弱根气不足，宜借印星比劫以扶身`;
  }
  return `${el}在${monthBranch}月${qi}气，身主中和，五行力量相对均衡`;
}

/**
 * 获取术数化性格总结
 */
export function getTraitSummary(fourPillars: FourPillars): TraitSummary {
  const stem = fourPillars.day.stem;
  const bodyStrength = calcDayStrength(fourPillars);
  const shishenDist = calcShishenDistribution(fourPillars);
  const dominantShishen = (shishenDist[0]?.name ?? '比肩') as Shishen;
  const geju = judgeGeju(fourPillars);
  const monthBranch = fourPillars.month.branch;

  return {
    typeDesc: STEM_YY_SHORT[stem],
    yinYang: `${stem}为${STEM_YIN_YANG[stem]}${STEM_ELEMENT[stem]}`,
    wangShuai: calcWangShuai(stem, monthBranch, bodyStrength),
    shishenEffect: `${dominantShishen}主导，${SHISHEN_BEHAVIOR[dominantShishen]?.slice(0, 30) ?? '行事风格受其影响'}…`,
    risk: `${geju.name}格局，${GEJU_RISK_TAGS[geju.name]?.[0] ?? '格局风险需关注'}`,
  };
}